/**
 * prospector-visible — ONE predicate for what the Prospector shows, used by the
 * list query AND by the nav pill, so the number and the page can never disagree.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHY THIS FILE EXISTS.
 *
 * The buyer-intent judge tested 171 prospector rows against the user's own
 * buyer statement and wrote "no" onto them. Nothing acted on the verdict, so
 * they stayed on the page. A verdict a screen has to remember to honour is not
 * a verdict; it is a column.
 *
 * Two things fixed that. The judge now archives on "no" in the same code path
 * that writes the verdict (scripts/intent-judge.mjs). And this file holds the
 * only description of what survives, so the pill count and the list are the
 * same query written once.
 *
 * THE PREDICATE, in words: not archived, and not judged "no". A row never
 * judged (intent_match IS NULL) still shows — it says so on its face rather
 * than being hidden on a verdict nobody ever passed.
 *
 * REACHABILITY is separate and is a USER-FACING FILTER, default on: a person
 * with no address and no profile to open is a road to nowhere. It is applied
 * to the list and to the count together, through passesProspectorRow, so
 * turning it off changes what you see and what the pill promises in the same
 * step.
 * ─────────────────────────────────────────────────────────────────────────────
 */

/** The lane the Prospector renders. Bare /dashboard/dealflow shows every lane. */
export const PROSPECTOR_KIND = "harvest_candidate";

/**
 * THE DATABASE HALF of the predicate. Applied to a PostgREST query builder.
 *
 * archived_at IS NULL AND (intent_match IS NULL OR intent_match <> 'no')
 */
export function applyProspectorVisible<T>(query: T): T {
  return (query as any)
    .is("archived_at", null)
    .or("intent_match.is.null,intent_match.neq.no") as T;
}

/** The columns the count needs. The list selects everything; this selects the predicate. */
export const PROSPECTOR_COUNT_SELECT =
  "id,status,source_type,entry_reason,entry_reason_kind,mention_text,contact_email,primary_email," +
  "linkedin_url,twitter_handle,phone,telegram,whatsapp,mention_url,custom_fields";

/** The non-email channels that count as a way to reach somebody. */
export const REACH_FIELDS: { key: string; label: string; get: (d: any) => string }[] = [
  { key: "x", label: "X", get: (d) => d?.twitter_handle || "" },
  { key: "linkedin", label: "LinkedIn", get: (d) => d?.linkedin_url || "" },
  { key: "phone", label: "phone", get: (d) => d?.phone || "" },
  { key: "telegram", label: "Telegram", get: (d) => d?.telegram || "" },
  { key: "whatsapp", label: "WhatsApp", get: (d) => d?.whatsapp || "" },
  { key: "instagram", label: "Instagram", get: (d) => (d?.custom_fields && d.custom_fields.instagram) || "" },
];

export function rowHasEmail(d: any): boolean {
  return !!String(d?.contact_email || d?.primary_email || "").trim();
}

/**
 * A working profile link — something a person can be opened at. A bare mention
 * URL counts only when it is an http(s) address; an empty string, a fragment or
 * a stored "null" is not a route to anybody.
 */
export function rowHasProfileLink(d: any): boolean {
  if (REACH_FIELDS.some((c) => String(c.get(d) || "").trim())) return true;
  const u = String(d?.mention_url || "").trim();
  return /^https?:\/\/\S+\./i.test(u);
}

/** No address and no profile to open = a road to nowhere. */
export function rowIsReachable(d: any): boolean {
  return rowHasEmail(d) || rowHasProfileLink(d);
}

/**
 * The statuses that mean "still waiting on you". The Prospector is the freshly
 * harvested queue; a candidate that has been written to, replied to or won has
 * moved on to the CRM lane and is not waiting here any more.
 */
export const PROSPECTOR_WAITING_STATUSES = ["new", "inbox", "in_play"];

/**
 * EVIDENCE — the thing that makes a row a discovery result rather than a name.
 *
 * A quote from where we found them, or the text of the mention itself. Either
 * one lets the card say what this person did; neither leaves the system with
 * nothing to say about somebody it is putting in front of you.
 */
export function rowHasEvidence(d: any): boolean {
  return !!String(d?.entry_reason || "").trim() || !!String(d?.mention_text || "").trim();
}

/**
 * THE CLIENT HALF of the predicate.
 *
 * ── TWO DIFFERENT QUESTIONS, AND THEY WERE BRIEFLY ONE ────────────────────
 *
 * Some of this is HYGIENE and applies to every dealflow lane: a row explicitly
 * archived, auto-killed, or seeded as a demo is not a lead anywhere.
 *
 * The rest is what makes a row a DISCOVERY RESULT, and applies only to the
 * Prospector: it must still be waiting, and the system must be able to say
 * something about why the person is there.
 *
 * Applying the discovery tests to every lane hid 41 hand-imported contacts —
 * Jay Kreps, Spenser Skates, Derek Andersen, each with a real address — from
 * the CRM board, because a bulk import has no entry_reason and never should.
 * An import is not a failed discovery result. It belongs in Contacts, and it
 * belongs on the pipeline it was imported into; it has no business on a
 * discovery page and it must not vanish from the others to keep it off.
 *
 * `discovery` is passed by BOTH the pill and the Prospector list and by neither
 * of the other lanes — one predicate, two callers, the difference in an
 * argument they both pass rather than in code only one of them runs.
 */
export function passesProspectorRow(
  d: any,
  opts?: { reachableOnly?: boolean; discovery?: boolean; waitingOnly?: boolean },
): boolean {
  const status = String(d?.status || "");

  // ── hygiene: every lane ──────────────────────────────────────────────────
  if (status === "archived") return false;
  if (status.startsWith("auto_killed")) return false;
  if (d?.source_type === "manual_demo") return false;

  // ── discovery: the Prospector only ───────────────────────────────────────
  // waitingOnly is kept as an alias so an older caller cannot silently lose the
  // discovery tests by using the previous name.
  const discovery = opts?.discovery ?? opts?.waitingOnly ?? false;
  if (discovery) {
    if (!PROSPECTOR_WAITING_STATUSES.includes(status)) return false;
    if (String(d?.entry_reason_kind || "unknown") === "unknown") return false;
    // THE EVIDENCE TEST. A row the system cannot say anything about has no
    // business on a discovery page, however good the name on it is.
    if (!rowHasEvidence(d)) return false;
  }

  if (opts?.reachableOnly !== false && !rowIsReachable(d)) return false;
  return true;
}

/**
 * THE COUNT BEHIND THE NAV PILL. Same lane, same database predicate, same row
 * test as the page. It is a real query rather than a server counter precisely
 * so there is one description of "visible" and not two.
 */
export async function countProspectorVisible(
  supabase: any,
  userId: string,
  opts?: { reachableOnly?: boolean },
): Promise<number | null> {
  // The status set is NOT filtered here. passesProspectorRow is the one arbiter
  // of what "waiting" means, and it is the function the list calls too.
  let q = supabase
    .from("dealflow")
    .select(PROSPECTOR_COUNT_SELECT)
    .eq("user_id", userId)
    .is("deleted_at", null)
    .eq("dealflow_kind", PROSPECTOR_KIND);
  q = applyProspectorVisible(q);
  const { data, error } = await q;
  if (error) return null;                       // a count it cannot make is no pill, never a guess
  return ((data as any[]) || [])
    .filter((r) => passesProspectorRow(r, { ...opts, discovery: true }))
    .length;
}

/**
 * The same predicate in SQL, for public.nav_counters.count_sql. Kept beside the
 * client version so a change to one is a change in the same file as the other;
 * scripts/check-prospector-predicate.mjs fails the build if the row in the
 * database has drifted from this string.
 */
export const PROSPECTOR_COUNT_SQL = `select count(*) from public.dealflow d
     where d.user_id = auth.uid() and d.archived_at is null and d.deleted_at is null
       and d.dealflow_kind = 'harvest_candidate'
       and d.status in ('new','inbox','in_play')
       and coalesce(d.entry_reason_kind,'unknown') <> 'unknown'
       and (coalesce(d.entry_reason,'') <> '' or coalesce(d.mention_text,'') <> '')
       and (d.intent_match is null or d.intent_match <> 'no')
       and (coalesce(d.contact_email,'') <> '' or coalesce(d.primary_email,'') <> ''
            or coalesce(d.linkedin_url,'') <> '' or coalesce(d.twitter_handle,'') <> ''
            or coalesce(d.phone,'') <> '' or coalesce(d.telegram,'') <> ''
            or coalesce(d.whatsapp,'') <> '' or d.mention_url ~* '^https?://\\S+\\.')`;

/** What the "why this is here" line says when nothing has judged the row yet. */
export const NOT_YET_CHECKED = "Not yet checked against your buyer statement";
